import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Trash2, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin-only delete button. Renders nothing for non-admin roles.
 *
 * Props:
 *  - endpoint: full API path, e.g. `/inquiries/${id}` (no /api prefix)
 *  - entityName: "Inquiry" | "Quotation" | ...
 *  - entityNumber: "INQ/26-27/00019" (displayed in confirm)
 *  - onDeleted: () => void  (called after successful delete)
 *  - redirectAfter: string  (route to navigate to after delete, e.g. "/inquiries")
 *  - allowCascade: boolean  (show a "Cascade delete children" checkbox)
 *  - size: "sm" | "default"  (default: "sm")
 */
export default function AdminDeleteButton({
  endpoint, entityName, entityNumber, onDeleted, redirectAfter, allowCascade = true, size = "sm",
}) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [cascade, setCascade] = useState(false);
  const [blockers, setBlockers] = useState(null);
  const [busy, setBusy] = useState(false);

  if (!user || !["admin", "super_admin"].includes(user.role)) return null;

  async function doDelete() {
    setBusy(true);
    setBlockers(null);
    try {
      const url = endpoint + (cascade ? "?cascade=true" : "");
      const { data } = await api.delete(url);
      toast.success(`${entityName} ${entityNumber || ""} deleted permanently.`);
      setOpen(false);
      if (onDeleted) onDeleted(data);
      if (redirectAfter) nav(redirectAfter);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.blockers) {
        setBlockers(detail.blockers);
      } else {
        toast.error(formatApiErrorDetail(detail) || `Failed to delete ${entityName}`);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Button
        size={size}
        variant="outline"
        className="border-red-200 text-red-700 hover:bg-red-50"
        onClick={() => { setBlockers(null); setCascade(false); setOpen(true); }}
        data-testid={`admin-delete-${entityName.toLowerCase().replace(/\s/g, "-")}`}
      >
        <Trash2 className="w-3.5 h-3.5 mr-1" />
        Delete
      </Button>

      <Dialog open={open} onOpenChange={(o) => !o && !busy && setOpen(false)}>
        <DialogContent data-testid="admin-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Confirm Delete
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete this record? <strong className="text-red-700">This action cannot be undone.</strong>
            </DialogDescription>
          </DialogHeader>

          <div className="text-sm space-y-3">
            <div>
              You are about to delete{" "}
              <span className="font-semibold">{entityName}</span>{" "}
              <span className="font-mono bg-slate-100 rounded px-2 py-0.5">{entityNumber || "record"}</span>.
            </div>

            {blockers && (
              <div className="border border-amber-200 bg-amber-50 rounded p-3 text-xs">
                <div className="font-semibold text-amber-800 mb-1">Cannot delete — this record has dependent data:</div>
                <ul className="list-disc list-inside space-y-0.5 text-amber-900">
                  {blockers.map((b, i) => <li key={i}>{b}</li>)}
                </ul>
                {allowCascade && (
                  <label className="flex items-center gap-2 mt-3 text-slate-800 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={cascade}
                      onChange={(e) => setCascade(e.target.checked)}
                      data-testid="cascade-checkbox"
                    />
                    <span>Cascade delete — also permanently delete all dependent records above.</span>
                  </label>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
            <Button
              className="bg-red-700 hover:bg-red-800 text-white"
              onClick={doDelete}
              disabled={busy || (blockers && !cascade)}
              data-testid="btn-confirm-delete"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              {cascade ? "Cascade Delete" : "Delete Permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
